import {
  ColDef,
  GetGroupRowAggParams,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  Currency,
  DspAdgroupStateEnum,
  DspCampaignStateEnum,
  DspCreativeAdCreativeFormatPropertiesAdCreativeFormatTypeEnum,
  DspDeliveryStatus,
  DspStats,
} from "@front/m19-api-client";
import { getFilteredLeafNodes, gridConfig, SIDE_BAR_NO_PIVOT, sideBar } from "@front/m19-grid-config";
import { Metric } from "@front/m19-metrics";
import { AuthService, UserSelectionService } from "@front/m19-services";
import { emptyDspStats, sumDspStats } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { currencyColumn } from "@m19-board/grid-config/grid-columns";
import { ACTIONS_COL_ID, exportGridCsv } from "@m19-board/grid-config/grid-config";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { DspDisplayMetrics } from "@m19-board/models/DspMetrics";

export type DspCreativeStats = DspStats & {
  dspCreativeName?: string;
  type?: DspCreativeAdCreativeFormatPropertiesAdCreativeFormatTypeEnum;
  lineItemStats: DspStats[];
  stats?: DspStats[];
  previousData?: DspStats[];
  previousTotalData?: DspStats;
};

export const DspCampaignState: Record<DspCampaignStateEnum, string> = {
  [DspCampaignStateEnum.ACTIVE]: "dsp-stats.campaignstate_active",
  [DspCampaignStateEnum.INACTIVE]: "dsp-stats.campaignstate_inactive",
};

export const DspLineItemState: Record<DspAdgroupStateEnum, string> = {
  [DspAdgroupStateEnum.ENABLED]: "dsp-stats.adgroupstate_active",
  [DspAdgroupStateEnum.PAUSED]: "dsp-stats.adgroupstate_inactive",
};

export const DspDeliveryStatusDesc: Record<DspDeliveryStatus, string> = {
  [DspDeliveryStatus.DELIVERING]: "dsp-stats.dspCampaignDeliveryStatus_DELIVERING",
  [DspDeliveryStatus.NOT_DELIVERING]: "dsp-stats.dspCampaignDeliveryStatus_NOT_DELIVERING",
  [DspDeliveryStatus.ENDED]: "dsp-stats.dspCampaignDeliveryStatus_ENDED",
  [DspDeliveryStatus.ADGROUPS_NOT_RUNNING]: "dsp-stats.dspCampaignDeliveryStatus_ADGROUPS_NOT_RUNNING",
  [DspDeliveryStatus.CREATIVES_NOT_RUNNING]: "dsp-stats.dspCampaignDeliveryStatus_CREATIVES_NOT_RUNNING",
  [DspDeliveryStatus.READY_TO_DELIVER]: "dsp-stats.dspCampaignDeliveryStatus_READY_TO_DELIVER",
  [DspDeliveryStatus.SUSPENDED]: "dsp-stats.dspCampaignDeliveryStatus_SUSPENDED",
  [DspDeliveryStatus.INACTIVE]: "dsp-stats.dspCampaignDeliveryStatus_INACTIVE",
  [DspDeliveryStatus.OTHER]: "dsp-stats.dspCampaignDeliveryStatus_OTHER",
};

export function dspGridOptions<T extends DspStats>(options: {
  gridKey: string;
  autoSizeDefault?: boolean;
  withRowGrouping?: boolean;
}): GridOptions<T> {
  return {
    defaultColDef: {
      sortable: true,
      resizable: true,
      filter: true,
    },
    ...gridConfig({
      gridKey: options.gridKey,
      withFooterRow: true,
      autoFitDefault: false,
      autoSizeDefault: options.autoSizeDefault,
      aggFunction: sumDspStats,
    }),
    ...(options.withRowGrouping
      ? {
          sideBar: sideBar(false, true, true),
          autoGroupColumnDef: { pinned: "left" as const },
          getGroupRowAgg: (params: GetGroupRowAggParams<T>) => {
            let result = emptyDspStats();
            params.nodes.forEach((node: IRowNode<T>) => {
              const childAgg = getFilteredLeafNodes(node)
                .map((node: IRowNode) => node.data)
                .reduce((prev, curr) => sumDspStats(prev, curr), emptyDspStats());
              result = sumDspStats(result, childAgg);
            });
            return result;
          },
        }
      : { sideBar: SIDE_BAR_NO_PIVOT }),
  };
}

@Injectable({ providedIn: "root" })
export class DspGridColumnsService {
  private readonly translocoService = inject(TranslocoService);
  private readonly gridConfigService = inject(GridConfigService);
  private readonly authService = inject(AuthService);
  private readonly userSelectionService = inject(UserSelectionService);

  private readonly locale = toSignal(this.authService.userLocale$, { requireSync: true });
  private readonly currency = toSignal(this.userSelectionService.selectedCurrency$, { requireSync: true });

  metricsColumns<T>(
    previousData?: (params: ICellRendererParams<T>) => DspStats | undefined,
    metrics: Metric<DspStats>[] = DspDisplayMetrics,
    defaultMetrics: Metric<DspStats>[] = metrics,
  ): ColDef<T>[] {
    const defaultMetricIds = new Set(defaultMetrics.map((m) => m.id));
    return (this.gridConfigService.getMetricsColDef(metrics) as ColDef<T>[]).map((def) => ({
      ...def,
      hide: !defaultMetricIds.has(def.colId!),
      cellRendererParams: (params: ICellRendererParams<T>) => ({
        ...def.cellRendererParams(params),
        currency: this.currency(),
        locale: this.locale(),
        previousData: previousData?.(params),
      }),
    }));
  }

  currencyColumn<T>(): ColDef<T, Currency> {
    return {
      ...currencyColumn<T>(() => this.currency()),
      headerName: this.translocoService.translate("common.currency"),
      headerValueGetter: () => this.translocoService.translate("common.currency"),
    };
  }

  lineItemNameColumn<T extends { name?: string }>(): ColDef<T, string> {
    return {
      ...this.textHeader("dsp-stats.line_item"),
      colId: "name",
      valueGetter: (params: ValueGetterParams<T, string>) => params.data?.name,
      floatingFilter: true,
      filter: "agTextColumnFilter",
      pinned: "left",
    };
  }

  lineItemStateColumn<T extends { state?: DspAdgroupStateEnum }>(options?: {
    enableRowGroup?: boolean;
  }): ColDef<T, DspAdgroupStateEnum> {
    return {
      ...this.textHeader("dsp-stats.state"),
      colId: "state",
      valueGetter: (params: ValueGetterParams<T, DspAdgroupStateEnum>) => params.data?.state,
      pinned: "left",
      valueFormatter: (params: ValueFormatterParams<T, DspAdgroupStateEnum>) =>
        params.value ? this.translocoService.translate(DspLineItemState[params.value]) : "",
      floatingFilter: true,
      filter: "agSetColumnFilter",
      filterValueGetter: (params: ValueGetterParams<T, DspAdgroupStateEnum>) =>
        params.data?.state ? this.translocoService.translate(DspLineItemState[params.data.state]) : "",
      enableRowGroup: options?.enableRowGroup,
    };
  }

  deliveryStatusColumn<T extends { deliveryStatus?: DspDeliveryStatus }>(options?: {
    enableRowGroup?: boolean;
  }): ColDef<T, DspDeliveryStatus> {
    return {
      ...this.textHeader("dsp-stats.deliveryStatus"),
      colId: "deliveryStatus",
      valueGetter: (params: ValueGetterParams<T, DspDeliveryStatus>) => params.data?.deliveryStatus,
      pinned: "left",
      valueFormatter: (params: ValueFormatterParams<T, DspDeliveryStatus>) =>
        params.value ? this.translocoService.translate(DspDeliveryStatusDesc[params.value]) : "",
      filterValueGetter: (params: ValueGetterParams<T, DspDeliveryStatus>) =>
        params.data?.deliveryStatus
          ? this.translocoService.translate(DspDeliveryStatusDesc[params.data.deliveryStatus])
          : "",
      floatingFilter: true,
      filter: "agSetColumnFilter",
      enableRowGroup: options?.enableRowGroup,
    };
  }

  creativeNameColumn<T extends { dspCreativeName?: string }>(): ColDef<T, string> {
    return {
      ...this.textHeader("dsp-stats.creative"),
      colId: "dspCreativeName",
      valueGetter: (params: ValueGetterParams<T, string>) => params.data?.dspCreativeName,
      floatingFilter: true,
      filter: "agTextColumnFilter",
      pinned: "left",
      valueFormatter: (params: ValueFormatterParams<T, string>) => {
        if (!params.data || params.node?.isRowPinned()) {
          return "";
        }
        return params.value ?? "Unknown";
      },
    };
  }

  creativeTypeColumn<T>(options: {
    colId: string;
    value: (data: T) => string | undefined;
    enableRowGroup?: boolean;
  }): ColDef<T, string> {
    return {
      ...this.textHeader("dsp-stats.creativeType"),
      colId: options.colId,
      valueGetter: (params: ValueGetterParams<T, string>) => (params.data ? options.value(params.data) : undefined),
      pinned: "left",
      floatingFilter: true,
      filter: "agSetColumnFilter",
      enableRowGroup: options.enableRowGroup,
    };
  }

  exportCsv(gridApi: GridApi, options: { fileName: string; columnHeaders: Record<string, string> }): void {
    const columnKeys = gridApi
      .getAllDisplayedColumns()
      .map((c) => c.getColId())
      .filter((c) => c !== ACTIONS_COL_ID)
      .concat(["currency"]);
    const headers = { ...options.columnHeaders, currency: "common.currency" };
    for (const [colId, translationKey] of Object.entries(headers)) {
      const columnDef = gridApi.getColumnDef(colId);
      if (columnDef) {
        columnDef.headerName = this.translocoService.translate(translationKey);
      }
    }
    exportGridCsv(gridApi, { fileName: options.fileName, columnKeys, skipColumnGroupHeaders: true });
  }

  private textHeader<T>(translationKey: string): Pick<ColDef<T>, "headerName" | "headerValueGetter"> {
    return {
      headerName: this.translocoService.translate(translationKey),
      headerValueGetter: () => this.translocoService.translate(translationKey),
    };
  }
}
